import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { usePostHog } from 'posthog-js/react';

const MailerliteSubscribeForm = ({ 
  title = "Curious what's next?", 
  description = "We send you what we'd want outrselves. Always beatiful." 
}) => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const posthog = usePostHog();
  
  // Get authentication state from Redux store
  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Submit to MailerLite endpoint
      const formData = new FormData();
      formData.append('fields[email]', email);
      formData.append('ml-submit', '1');
      formData.append('anticsrf', 'true');

      const response = await fetch('https://assets.mailerlite.com/jsonp/1365877/forms/158782149952014056/subscribe', {
        method: 'POST',
        body: formData,
        mode: 'no-cors' // MailerLite handles CORS differently
      });

      // Since we're using no-cors, we can't read the response, so we assume success
      
      // PostHog integration for email capture
      if (posthog && email) {
        // Only identify user if they are not logged in (not authenticated)
        // If user is logged in, they should already be identified in PostHog
        if (!isAuthenticated) {
          // User is not logged in, so identify them with their email
          posthog.identify(email, {
            email: email,
            source: 'email_subscription',
            subscription_location: 'footer_stay_updated'
          });
        }
        
        // Track the subscription event for all users
        posthog.capture('email_subscription', {
          email: email,
          subscription_source: 'mailerlite_form',
          location: 'footer_stay_updated'
        });
      }
      
      setIsSubmitted(true);
      setEmail('');
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error('Subscription error:', err);
      
      // Track subscription error
      if (posthog) {
        posthog.capture('email_subscription_error', {
          error_message: err.message,
          location: 'footer_stay_updated'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="text-center p-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
          <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>
        <h4 className="text-xl font-semibold text-gray-900 mb-2">Welcome to the collection!</h4>
        <p className="text-gray-600">You'll receive your first curated selection soon.</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      {title && (
        <h4 className="text-lg font-semibold text-gray-900 mb-2 text-center">{title}</h4>
      )}
      {description && (
        <p className="text-gray-600 mb-4 text-center text-sm">{description}</p>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            className="w-full px-4 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[var(marketplaceColor)] focus:border-[var(marketplaceColor)] transition-colors duration-200 placeholder-gray-500 text-base"
            required
            autoComplete="email"
            disabled={isLoading}
          />
          {error && (
            <p className="mt-2 text-sm text-red-600">{error}</p>
          )}
        </div>
        
        <button
          type="submit"
          disabled={isLoading || !email}
          className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-4 px-6 rounded-xl transition-colors duration-200 flex items-center justify-center text-base"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Subscribing...
            </>
          ) : (
            'Submit'
          )}
        </button>
      </form>
    </div>
  );
};

export default MailerliteSubscribeForm; 